import type { QuestionnaireSourceRecordContract } from '@/contracts/customer-application-binding'
import {
  type CustomerApplication,
  isReviewableApplicationStatus,
  normalizeEmail,
} from '@/lib/business-workflow'

export type DirectReviewLinkPayload = {
  formId: string
  email: string
  applicationId?: string
  sourceRecordId?: string
}

export class DirectReviewLinkTokenError extends Error {
  constructor(message = '無法解析直接審核連結參數。') {
    super(message)
    this.name = 'DirectReviewLinkTokenError'
  }
}

const tokenVersion = 'mf-direct-review-v1'
const tokenKey = 'miaoform-direct-review-link-mock-key-v1'

function normalizePayload(input: DirectReviewLinkPayload): DirectReviewLinkPayload {
  const formId = input.formId.trim()
  const email = normalizeEmail(input.email)
  const applicationId = input.applicationId?.trim()
  const sourceRecordId = input.sourceRecordId?.trim()
  if (!formId || !email) throw new DirectReviewLinkTokenError()
  return {
    formId,
    email,
    ...(applicationId ? { applicationId } : {}),
    ...(sourceRecordId ? { sourceRecordId } : {}),
  }
}

function xorBytes(bytes: Uint8Array) {
  const key = new TextEncoder().encode(tokenKey)
  return bytes.map((byte, index) => byte ^ key[index % key.length])
}

function bytesToBase64Url(bytes: Uint8Array) {
  let base64 = ''
  if (typeof Buffer !== 'undefined') {
    base64 = Buffer.from(bytes).toString('base64')
  } else {
    let binary = ''
    for (const byte of bytes) binary += String.fromCharCode(byte)
    base64 = btoa(binary)
  }
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function base64UrlToBytes(value: string) {
  const padded = value
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=')
  if (typeof Buffer !== 'undefined') return new Uint8Array(Buffer.from(padded, 'base64'))
  const binary = atob(padded)
  return Uint8Array.from(binary, (char) => char.charCodeAt(0))
}

export function createDirectReviewToken(input: DirectReviewLinkPayload) {
  const payload = normalizePayload(input)
  const raw = JSON.stringify({
    v: tokenVersion,
    formId: payload.formId,
    email: payload.email,
    applicationId: payload.applicationId,
    sourceRecordId: payload.sourceRecordId,
  })
  return bytesToBase64Url(xorBytes(new TextEncoder().encode(raw)))
}

export function parseDirectReviewToken(token: string): DirectReviewLinkPayload {
  try {
    const bytes = xorBytes(base64UrlToBytes(token.trim()))
    const parsed = JSON.parse(new TextDecoder().decode(bytes)) as {
      v?: string
      formId?: string
      email?: string
      applicationId?: string
      sourceRecordId?: string
    }
    if (parsed.v !== tokenVersion || !parsed.formId || !parsed.email) {
      throw new DirectReviewLinkTokenError()
    }
    return normalizePayload({
      formId: parsed.formId,
      email: parsed.email,
      applicationId: parsed.applicationId,
      sourceRecordId: parsed.sourceRecordId,
    })
  } catch (error) {
    if (error instanceof DirectReviewLinkTokenError) throw error
    throw new DirectReviewLinkTokenError()
  }
}

export function buildDirectReviewQuestionnairePath(input: DirectReviewLinkPayload) {
  return `/review-links/questionnaire?token=${createDirectReviewToken(input)}`
}

export function buildDirectReviewApprovePath(input: DirectReviewLinkPayload) {
  return `/review-links/approve?token=${createDirectReviewToken(input)}`
}

export function resolveDirectReviewApplication(
  applications: CustomerApplication[],
  payload: DirectReviewLinkPayload,
) {
  const normalized = normalizePayload(payload)
  if (normalized.applicationId) {
    const application = applications.find((item) => item.id === normalized.applicationId)
    if (application) return application
  }
  const candidates = applications.filter(
    (application) =>
      application.formId === normalized.formId &&
      (normalizeEmail(application.contactEmail) === normalized.email ||
        normalizeEmail(application.submittedByEmail) === normalized.email),
  )
  return (
    candidates.find((application) => isReviewableApplicationStatus(application.status)) ??
    candidates[0]
  )
}

export function resolveDirectReviewSourceRecord(
  records: QuestionnaireSourceRecordContract[],
  payload: DirectReviewLinkPayload,
) {
  const normalized = normalizePayload(payload)
  if (normalized.sourceRecordId) {
    const record = records.find((item) => item.id === normalized.sourceRecordId)
    if (record) return record
  }
  return records.find(
    (record) =>
      record.formId === normalized.formId &&
      normalizeEmail(record.submittedByEmail) === normalized.email,
  )
}

export function parseDirectReviewPayloadFromSearch(search: string) {
  const token = new URLSearchParams(search).get('token')
  if (!token) throw new DirectReviewLinkTokenError('直接審核連結缺少 token 參數。')
  return parseDirectReviewToken(token)
}
